import { pageQuery, type PageOpts } from "./paging";
import { api } from "./api";

function base(orgId: string): string {
  return `/org/${orgId}/crm`;
}

export const opsApi = {
  listTerritories: (orgId: string) => api.get<{ territories: Territory[] }>(`${base(orgId)}/territories`),
  createTerritory: (orgId: string, body: {
    code: string;
    name: string;
    timezone?: string | null;
    currency?: string | null;
    currency_symbol?: string | null;
    country?: string | null;
    date_format?: string | null;
    time_format?: string | null;
    number_format?: string | null;
    forex_pull_enabled?: boolean;
    fiscal_year_start_month?: number;
    quote_approval_required?: boolean;
    tax_rate?: string | number | null;
    history_after_days?: number;
  }) => api.post<Territory>(`${base(orgId)}/territories`, body),
  updateTerritoryLocale: (orgId: string, territoryId: string, body: {
    timezone: string;
    currency: string;
    currency_symbol?: string | null;
    country?: string | null;
    date_format: string;
    time_format: string;
    number_format: string;
    forex_pull_enabled?: boolean;
    fiscal_year_start_month?: number;
    quote_approval_required?: boolean;
    tax_rate?: string | number | null;
    history_after_days?: number;
  }) => api.patch<Territory>(`${base(orgId)}/territories/${territoryId}`, body),
  fxRates: (orgId: string) => api.get<{ enabled: boolean; rates: FxRate[] }>(`${base(orgId)}/fx-rates`),
  assignTerritory: (orgId: string, territoryId: string, body: { user_id?: string; company_id?: string }) =>
    api.post<{ ok: boolean }>(`${base(orgId)}/territories/${territoryId}/assign`, body),

  listFields: (orgId: string, entity: string) =>
    api.get<{ entity: string; fields: FieldDef[] }>(`${base(orgId)}/fields/${entity}`),
  saveFields: (orgId: string, entity: string, fields: FieldDef[]) =>
    api.patch<{ entity: string; fields: FieldDef[] }>(`${base(orgId)}/fields/${entity}`, { fields }),

  listFieldOptions: (orgId: string) =>
    api.get<{ fields: ConfigurableFieldOptions[] }>(`${base(orgId)}/field-options`),
  saveFieldOptions: (orgId: string, entity: string, fieldKey: string, options: string[]) =>
    api.patch<ConfigurableFieldOptions>(`${base(orgId)}/field-options/${entity}/${fieldKey}`, { options }),

  getLayout: (orgId: string, entity: string) => api.get<AppLayout>(`${base(orgId)}/layouts/${entity}`),
  saveLayout: (orgId: string, entity: string, layout: AppLayout["layout"]) =>
    api.put<AppLayout>(`${base(orgId)}/layouts/${entity}`, { screen: "detail", layout }),
  resetLayout: (orgId: string, entity: string) =>
    api.post<AppLayout>(`${base(orgId)}/layouts/${entity}/reset`, {}),

  listDashboards: (orgId: string) =>
    api.get<{ dashboards: DesignedDashboard[]; datasets: string[] }>(`${base(orgId)}/designed-dashboards`),
  saveDashboard: (orgId: string, body: { name: string; widgets: DashboardWidget[] }, id?: string) =>
    id
      ? api.put<DesignedDashboard>(`${base(orgId)}/designed-dashboards/${id}`, body)
      : api.post<DesignedDashboard>(`${base(orgId)}/designed-dashboards`, body),

  listDocuments: (orgId: string, kind?: string) =>
    api.get<{ documents: DocumentTemplate[] }>(`${base(orgId)}/documents${kind ? `?kind=${kind}` : ""}`),
  saveDocument: (orgId: string, body: Partial<DocumentTemplate> & { kind: string; name: string }) =>
    api.post<DocumentTemplate>(`${base(orgId)}/documents`, body),
  previewDocument: (orgId: string, documentId: string, body?: { elements?: DocumentTemplate["elements"]; sample?: Record<string, unknown> }) =>
    api.post<{ html: string }>(`${base(orgId)}/documents/${documentId}/preview`, body ?? {}),
  previewRecord: (orgId: string, kind: string, recordId: string) =>
    api.get<{ html: string; kind: string; number?: string | null; page_size?: string }>(
      `${base(orgId)}/documents/${kind}/${recordId}/preview`,
    ),
  previewQuoteVersion: (orgId: string, quoteId: string, versionNumber: number) =>
    api.get<{ html: string; kind: string; number?: string | null; page_size?: string }>(
      `${base(orgId)}/documents/quote/${quoteId}/versions/${versionNumber}/preview`,
    ),
  uploadDocumentLogo: (orgId: string, file: File) =>
    api.upload<{ content_type: string; bytes: number }>(`${base(orgId)}/documents/logo`, file),
  getDocumentLogo: (orgId: string) => api.getBlob(`${base(orgId)}/documents/logo`),
  deleteDocumentLogo: (orgId: string) => api.delete<{ ok: boolean }>(`${base(orgId)}/documents/logo`),

  listCycleTemplates: (orgId: string) =>
    api.get<{ templates: CycleTemplate[] }>(`${base(orgId)}/sales-cycle/templates`),
  ensureTerritoryCycle: (orgId: string, territoryId: string) =>
    api.post<CycleTemplate>(`${base(orgId)}/sales-cycle/templates/for-territory`, { territory_id: territoryId }),
  saveCycleTemplate: (orgId: string, templateId: string, body: CycleTemplateSave) =>
    api.patch<CycleTemplate>(`${base(orgId)}/sales-cycle/templates/${templateId}`, body),
  listCycleRuns: (orgId: string) => api.get<{ runs: CycleRun[] }>(`${base(orgId)}/sales-cycle/runs`),
  startCycle: (orgId: string, companyId: string, templateId?: string) =>
    api.post<CycleRunDetail>(`${base(orgId)}/sales-cycle/runs`, { company_id: companyId, template_id: templateId }),
  getCycleRun: (orgId: string, runId: string) => api.get<CycleRunDetail>(`${base(orgId)}/sales-cycle/runs/${runId}`),
  deleteCycleRun: (orgId: string, runId: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/sales-cycle/runs/${runId}`),
  updateCycleTask: (orgId: string, runId: string, taskId: string, body: { status?: string; comment?: string }) =>
    api.post<CycleRunDetail>(`${base(orgId)}/sales-cycle/runs/${runId}/tasks/${taskId}`, body),
  updateCycleStage: (orgId: string, runId: string, stageId: string, status: "started" | "not_started") =>
    api.patch<CycleRunDetail>(`${base(orgId)}/sales-cycle/runs/${runId}/stages/${stageId}`, { status }),

  listTickets: (orgId: string, opts?: { companyId?: string; status?: string; q?: string } | string) => {
    const params = new URLSearchParams();
    if (typeof opts === "string") {
      if (opts) params.set("company_id", opts);
    } else if (opts) {
      if (opts.companyId) params.set("company_id", opts.companyId);
      if (opts.status) params.set("status_filter", opts.status);
      if (opts.q) params.set("q", opts.q);
    }
    const qs = params.toString();
    return api.get<{ tickets: SupportTicket[] }>(`${base(orgId)}/support${qs ? `?${qs}` : ""}`);
  },
  createTicket: (
    orgId: string,
    body: {
      company_id: string;
      contact_id: string;
      title: string;
      description: string;
      notify_user?: boolean;
    },
  ) => api.post<SupportTicket>(`${base(orgId)}/support`, body),
  getTicket: (orgId: string, ticketId: string) => api.get<SupportTicket & { comments: SupportComment[] }>(`${base(orgId)}/support/${ticketId}`),
  deleteTicket: (orgId: string, ticketId: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/support/${ticketId}`),
  patchTicket: (orgId: string, ticketId: string, body: { status?: string; comment?: string; notify_user?: boolean; title?: string; description?: string }) =>
    api.patch<SupportTicket & { comments: SupportComment[] }>(`${base(orgId)}/support/${ticketId}`, body),
  generateLeadFromTicket: (
    orgId: string,
    ticketId: string,
    body?: {
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      lead_score?: number | null;
      rating?: string;
      notes?: string;
      product_ids?: string[];
    },
  ) =>
    api.post<SupportTicket & { comments: SupportComment[] }>(`${base(orgId)}/support/${ticketId}/generate-lead`, body ?? {}),

  listInvoices: (orgId: string, q?: string, page?: PageOpts, companyId?: string) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (companyId) params.set("company_id", companyId);
    pageQuery(params, page);
    const qs = params.toString();
    return api.get<{ invoices: Invoice[]; total?: number }>(`${base(orgId)}/invoices${qs ? `?${qs}` : ""}`);
  },
  getInvoice: (orgId: string, invoiceId: string) => api.get<Invoice>(`${base(orgId)}/invoices/${invoiceId}`),
  deleteInvoice: (orgId: string, invoiceId: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/invoices/${invoiceId}`),
  createInvoice: (orgId: string, orderId: string) => api.post<Invoice>(`${base(orgId)}/invoices`, { order_id: orderId }),
  payInvoice: (orgId: string, invoiceId: string, body: { payment_reference: string; payment_date: string }) =>
    api.post<Invoice>(`${base(orgId)}/invoices/${invoiceId}/pay`, body),
  unpayInvoice: (orgId: string, invoiceId: string) =>
    api.post<Invoice>(`${base(orgId)}/invoices/${invoiceId}/unpay`, {}),

  listEvents: (orgId: string) => api.get<{ events: CalendarEvent[] }>(`${base(orgId)}/calendar`),
  createEvent: (orgId: string, body: CalendarEventInput) => api.post<CalendarEvent>(`${base(orgId)}/calendar`, body),
  updateEvent: (orgId: string, eventId: string, body: Partial<CalendarEventInput>) =>
    api.patch<CalendarEvent>(`${base(orgId)}/calendar/${eventId}`, body),
  deleteEvent: (orgId: string, eventId: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/calendar/${eventId}`),

  listCampaigns: (orgId: string) => api.get<{ campaigns: Campaign[] }>(`${base(orgId)}/campaigns`),
  createCampaign: (orgId: string, body: { name: string; subject: string; body: string; territory_id?: string }) =>
    api.post<Campaign>(`${base(orgId)}/campaigns`, body),
  deleteCampaign: (orgId: string, campaignId: string) =>
    api.delete<{ id: string; soft_deleted: boolean }>(`${base(orgId)}/campaigns/${campaignId}`),
  campaignAudience: (orgId: string, territoryId?: string) =>
    api.get<{ count: number; sample: Array<{ email: string; name: string }> }>(
      `${base(orgId)}/campaigns/audience${territoryId ? `?territory_id=${territoryId}` : ""}`,
    ),
  scheduleCampaign: (orgId: string, campaignId: string, scheduledAt: string) =>
    api.post<Campaign>(`${base(orgId)}/campaigns/${campaignId}/schedule`, { scheduled_at: scheduledAt }),
  sendCampaign: (orgId: string, campaignId: string) =>
    api.post<Campaign>(`${base(orgId)}/campaigns/${campaignId}/send`, {}),
  downloadDocument: (orgId: string, kind: string, recordId: string, fmt = "pdf") =>
    api.download(`${base(orgId)}/documents/${kind}/${recordId}/download?fmt=${fmt}`, `${kind}.${fmt}`),
  createExportJob: (orgId: string, kind: string, recordId: string, idempotencyKey: string, fmt = "pdf") =>
    api.post<ExportJob>(
      `${base(orgId)}/documents/${kind}/${recordId}/export-jobs?fmt=${fmt}`,
      undefined,
      { "Idempotency-Key": idempotencyKey },
    ),
  getExportJob: (orgId: string, jobId: string) => api.get<ExportJob>(`${base(orgId)}/export-jobs/${jobId}`),
  downloadExportJob: (orgId: string, jobId: string, filename: string) =>
    api.download(`${base(orgId)}/export-jobs/${jobId}/download`, filename),
  getMailbox: (orgId: string) => api.get<{ mailbox: MailboxAccount | null }>(`${base(orgId)}/mailbox`),
  saveMailbox: (orgId: string, body: MailboxInput) => api.put<{ mailbox: MailboxAccount }>(`${base(orgId)}/mailbox`, body),
  testMailbox: (orgId: string) => api.post<{ ok: boolean }>(`${base(orgId)}/mailbox/test`, {}),
  getAiUsage: (orgId: string) => api.get<AiUsageSummary>(`${base(orgId)}/ai/usage`),
  getAiConnection: (orgId: string) => api.get<{ connection: AiConnection | null }>(`${base(orgId)}/ai/connection`),
  saveAiConnection: (orgId: string, body: { endpoint: string; model: string; api_key?: string | null }) =>
    api.put<{ connection: AiConnection }>(`${base(orgId)}/ai/connection`, body),
  deleteAiConnection: (orgId: string) => api.delete<{ removed: number }>(`${base(orgId)}/ai/connection`),
  testAiConnection: (orgId: string) => api.post<AiTestResult>(`${base(orgId)}/ai/connection/test`, {}),
  draftAi: (orgId: string, kind: string, prompt: string) =>
    api.post<{ text: string; cost_cents: number }>(`${base(orgId)}/ai/draft`, { kind, prompt }),

  getCompanyLifecycle: (orgId: string) =>
    api.get<CompanyLifecycleSettings>(`${base(orgId)}/company-lifecycle`),
  saveCompanyLifecycle: (orgId: string, body: CompanyLifecycleSettings) =>
    api.put<CompanyLifecycleSettings>(`${base(orgId)}/company-lifecycle`, body),

  listWorkflows: (orgId: string) => api.get<{ rules: WorkflowRule[] }>(`${base(orgId)}/workflows`),
  createWorkflow: (orgId: string, body: WorkflowRule) => api.post<WorkflowRule>(`${base(orgId)}/workflows`, body),
  patchWorkflow: (orgId: string, ruleId: string, body: Partial<WorkflowRule>) =>
    api.patch<WorkflowRule>(`${base(orgId)}/workflows/${ruleId}`, body),

  listNotifications: (orgId: string) =>
    api.get<{ notifications: AppNotification[] }>(`${base(orgId)}/notifications`),
  dismissNotification: (orgId: string, notificationId: string) =>
    api.post<{ ok: boolean }>(`${base(orgId)}/notifications/${notificationId}/dismiss`, {}),
  dismissAllNotifications: (orgId: string) =>
    api.post<{ ok: boolean; dismissed: number }>(`${base(orgId)}/notifications/dismiss-all`, {}),

  listTargets: (orgId: string) => api.get<{ targets: SalesTarget[] }>(`${base(orgId)}/targets`),
  createTarget: (orgId: string, body: SalesTargetInput) => api.post<SalesTarget>(`${base(orgId)}/targets`, body),
  deleteTargets: (orgId: string, body: Pick<SalesTargetInput, "subject_type" | "subject_id" | "period_start" | "period_end">) => {
    const params = new URLSearchParams({
      subject_type: body.subject_type,
      subject_id: body.subject_id,
      period_start: body.period_start,
      period_end: body.period_end,
    });
    return api.delete<{ deleted: number }>(`${base(orgId)}/targets?${params.toString()}`);
  },
  forecast: (orgId: string, year?: number, view?: "rollup" | "detail", territoryId?: string) => {
    const params = new URLSearchParams();
    if (year) params.set("year", String(year));
    if (view) params.set("view", view);
    if (territoryId && territoryId !== "all") params.set("territory_id", territoryId);
    const qs = params.toString();
    return api.get<Forecast>(`${base(orgId)}/forecast${qs ? `?${qs}` : ""}`);
  },
  listKpiItems: (orgId: string) =>
    api.get<{ items: KpiItem[]; changes: KpiItemChange[] }>(`${base(orgId)}/kpi-items`),
  createKpiItem: (orgId: string, body: { name: string; measurement: string; included_by_default: boolean }) =>
    api.post<KpiItem>(`${base(orgId)}/kpi-items`, body),
  updateKpiItem: (orgId: string, itemId: string, body: { name?: string; included_by_default?: boolean; retired?: boolean }) =>
    api.patch<KpiItem>(`${base(orgId)}/kpi-items/${itemId}`, body),
  getSalesPlan: (orgId: string, userId: string, year: number) =>
    api.get<SalesPlanResponse>(`${base(orgId)}/sales-plans/${userId}?year=${year}`),
  createSalesPlan: (
    orgId: string,
    userId: string,
    body: {
      fiscal_year: number;
      cadence: string;
      currency: string;
      territory_id?: string | null;
      start_month?: number | null;
    },
  ) => api.post<SalesPlanResponse>(`${base(orgId)}/sales-plans/${userId}`, body),
  updateSalesPlan: (orgId: string, userId: string, body: SalesPlanUpdate) =>
    api.put<SalesPlanResponse>(`${base(orgId)}/sales-plans/${userId}`, body),
  copySalesPlan: (
    orgId: string,
    userId: string,
    body: { fiscal_year: number; source_user_id: string; territory_id?: string | null },
  ) => api.post<SalesPlanResponse>(`${base(orgId)}/sales-plans/${userId}/copy`, body),
  agreeSalesPlan: (orgId: string, userId: string, year: number) =>
    api.post<SalesPlanResponse>(`${base(orgId)}/sales-plans/${userId}/agree?year=${year}`, {}),
  issueSalesPlan: (orgId: string, userId: string, year: number) =>
    api.post<{ issue_id: string }>(`${base(orgId)}/sales-plans/${userId}/issue?year=${year}`, {}),
  downloadSalesPlan: (orgId: string, userId: string, year: number, issueId?: string) => {
    const params = new URLSearchParams({ year: String(year) });
    if (issueId) params.set("issue_id", issueId);
    return api.download(`${base(orgId)}/sales-plans/${userId}/pdf?${params.toString()}`, `sales-plan-${year}.pdf`);
  },
  listStageEntryRules: (orgId: string) =>
    api.get<{ rules: StageEntryRule[] }>(`${base(orgId)}/stage-entry-rules`),
  saveStageEntryRules: (orgId: string, rules: StageEntryRule[]) =>
    api.put<{ rules: StageEntryRule[] }>(`${base(orgId)}/stage-entry-rules`, { rules }),
  listRecycle: (orgId: string) => api.get<{ items: RecycleItem[] }>(`${base(orgId)}/recycle-bin`),
  restoreRecycle: (orgId: string, source: string, id: string) =>
    api.post<{
      restored: boolean;
      renamed?: boolean;
      title?: string | null;
      original_title?: string | null;
      message?: string | null;
    }>(`${base(orgId)}/recycle-bin/restore`, { source, id }),
};

export interface Territory {
  id: string;
  code: string;
  name: string;
  active: boolean;
  timezone?: string | null;
  currency?: string | null;
  currency_symbol?: string | null;
  country?: string | null;
  date_format?: string | null;
  time_format?: string | null;
  number_format?: string | null;
  forex_pull_enabled?: boolean;
  fiscal_year_start_month?: number;
  quote_approval_required?: boolean;
  tax_rate?: number | null;
  history_after_days?: number;
}
export interface FxRate {
  base_currency: string;
  quote_currency: string;
  rate: string;
  as_of?: string | null;
  source?: string | null;
}
export interface ForecastAmount {
  currency: string;
  deals?: number;
  unweighted: string;
  weighted: string;
}
export interface ConfigurableFieldOptions {
  entity: string;
  field_key: string;
  label: string;
  list_key: string;
  options: string[];
  default_options: string[];
}
export interface FieldDef {
  field_key: string;
  label: string;
  field_type: string;
  native: boolean;
  visible: boolean;
  required: boolean;
  options?: unknown;
  ai_meaning?: string | null;
}
export interface LayoutFieldSlot {
  field_key: string;
  canvas_row: number;
  col_start: number;
  col_span: number;
}
export interface LayoutSection {
  key: string;
  label: string;
  fields: LayoutFieldSlot[];
}
export interface LayoutPage {
  key: string;
  label: string;
  kind?: "fields" | "related";
  sections?: LayoutSection[];
  fields: LayoutFieldSlot[];
}
export interface AppLayout {
  id: string | null;
  entity: string;
  screen: string;
  revision: number;
  status: string;
  layout: { pages: LayoutPage[] };
  fields: FieldDef[];
}
export interface DashboardWidget {
  id?: string;
  widget_type: string;
  title: string;
  dataset: string;
  field?: string | null;
  aggregation?: string | null;
  col_start: number;
  col_span: number;
  row_start: number;
  value?: string;
}
export interface DesignedDashboard {
  id: string;
  name: string;
  kind: string;
  is_default: boolean;
  widgets: DashboardWidget[];
}
export interface ExportJob {
  id: string;
  kind: string;
  record_id: string;
  fmt: string;
  status: "queued" | "processing" | "done" | "failed";
  filename: string | null;
  media_type: string | null;
  error: string | null;
  created_at: string | null;
  updated_at: string | null;
}
export interface DocumentTemplate {
  id: string;
  kind: string;
  name: string;
  page_size: string;
  elements: Array<{
    id: string;
    type: string;
    col_start: number;
    col_span: number;
    canvas_row: number;
    config?: {
      label?: string;
      field_key?: string;
      text?: string;
      description?: string;
      quantity?: number | string;
      unit_price?: number | string;
      amount?: number | string;
      rate?: number | string;
      op?: "percent" | "fixed";
      base?: "subtotal" | "total" | "tax_total" | "discount_total";
      apply_as?: "discount" | "tax" | "surcharge" | "display";
      discount_pct?: number | string;
    };
  }>;
}
export interface CycleTemplateTask {
  id: string;
  title: string;
  is_required: boolean;
  is_active?: boolean;
  sort_order?: number;
  estimate_hours?: number;
  estimate_unit?: "hours" | "days" | "weeks" | "months";
  estimate_amount?: number;
}
export interface CycleTemplate {
  id: string;
  name: string;
  territory_id?: string | null;
  stages: Array<{
    id: string;
    name: string;
    sort_order?: number;
    sla_hours: number | null;
    tasks: CycleTemplateTask[];
  }>;
}
export interface CycleTemplateSave {
  name?: string;
  stages?: Array<{
    id: string;
    name: string;
    sort_order?: number;
    sla_hours?: number | null;
    tasks?: Array<Partial<CycleTemplateTask> & { deleted?: boolean }>;
  }>;
}
export interface CycleRun {
  id: string;
  company_id: string;
  company_name: string;
  company_code?: string;
  status: string;
  started_at: string;
}
export interface CycleRunDetail extends CycleRun {
  stages: Array<{
    id: string;
    name: string;
    sla_hours?: number | null;
    flow_status?: "not_started" | "started" | "done" | null;
    starts_at?: string | null;
    ends_at?: string | null;
    flow_started_at?: string | null;
    tasks: Array<{
      id: string;
      title: string;
      status: "todo" | "doing" | "done";
      due_at?: string | null;
      estimate_amount?: number;
      estimate_unit?: "hours" | "days" | "weeks" | "months";
      comments: Array<{ id: string; body: string; created_at: string }>;
    }>;
  }>;
}
export interface SupportTicket {
  id: string;
  company_id: string;
  company_name: string;
  contact_id?: string | null;
  ticket_number?: string | null;
  title: string;
  description: string;
  status: string;
  opened_at: string;
  closed_at?: string | null;
  generated_lead_id?: string | null;
  generated_lead_code?: string | null;
  contact_first_name?: string | null;
  contact_last_name?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  comments?: SupportComment[];
}
export interface SupportComment { id: string; body: string; author_id: string; created_at: string; author_name?: string | null }
export interface Invoice {
  id: string;
  order_id: string;
  company_id?: string;
  company_name: string;
  order_number?: string | null;
  quote_id?: string | null;
  quote_number?: string | null;
  invoice_number: string;
  status: string;
  total: string | null;
  subtotal?: string | null;
  tax_total?: string | null;
  currency: string | null;
  lines?: Array<{
    description?: string | null;
    quantity?: string | number | null;
    unit_price?: string | number | null;
    discount_pct?: string | number | null;
    currency?: string | null;
    fx_rate?: string | number | null;
  }>;
  issued_at?: string | null;
  paid_at?: string | null;
  payment_reference?: string | null;
  payment_date?: string | null;
  created_at?: string | null;
  created_by_name?: string | null;
  sales_person_name?: string | null;
}
export interface CalendarEvent {
  id: string;
  company_id: string;
  company_name: string;
  title: string;
  starts_at: string;
  ends_at: string;
}
export interface CalendarEventInput {
  company_id: string;
  title: string;
  starts_at: string;
  ends_at: string;
  timezone?: string;
  notes?: string;
}
export interface Campaign {
  id: string;
  name: string;
  subject: string;
  body: string;
  status: string;
  scheduled_at?: string | null;
  audience?: { count?: number };
}
export interface CompanyLifecycleSettings {
  watch_leads: boolean;
  watch_opportunities: boolean;
  watch_quotes: boolean;
  watch_orders: boolean;
  watch_invoices: boolean;
  dormant_enabled: boolean;
  dormant_after_days: number;
  inactive_enabled: boolean;
  inactive_after_days: number;
  winback_enabled: boolean;
  winback_template_id: string | null;
}
export interface WorkflowRule {
  id?: string;
  kind?: string;
  name: string;
  entity: string;
  condition: Record<string, unknown>;
  action: Record<string, unknown>;
  enabled?: boolean;
  interval_days?: number | null;
}
export interface AppNotification {
  id: string;
  title: string;
  body: string;
  href?: string | null;
  due_at?: string | null;
  created_at?: string | null;
}
export interface SalesTarget {
  id: string;
  subject_type: string;
  subject_id: string;
  period_start: string;
  period_end: string;
  currency: string;
  amount: string;
}
export interface SalesTargetInput {
  subject_type: string;
  subject_id: string;
  period_start: string;
  period_end: string;
  currency: string;
  amount: string;
}
export interface KpiItem {
  id: string;
  slug: string;
  name: string;
  measurement: string;
  definition: string;
  included_by_default: boolean;
  sort_order: number;
  retired_at?: string | null;
  created_by_user_id?: string | null;
  updated_by_user_id?: string | null;
  created_by_email?: string | null;
  updated_by_email?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}
export interface KpiItemChange {
  id: string;
  item_id: string;
  actor_user_id?: string | null;
  actor_email?: string | null;
  field_name: string;
  old_value?: string | null;
  new_value?: string | null;
  created_at?: string | null;
}
export interface SalesPlanLine {
  item_id: string;
  name: string;
  measurement: string;
  definition: string;
  retired: boolean;
  target_count: string;
  actual: string;
  gap: string;
}
export interface SalesPlanCustom {
  id: string;
  title: string;
  description: string;
  hours: string;
}
export interface SalesPlanPeriod {
  id: string;
  period_start: string;
  period_end: string;
  label: string;
  locked: boolean;
  sales_target: string;
  sales_actual: string;
  sales_gap: string;
  lines: SalesPlanLine[];
  customs?: SalesPlanCustom[];
}
export interface SalesPlanMilestone {
  id: string;
  title: string;
  notes: string;
  confirmed: boolean;
  confirmed_at?: string | null;
  confirmed_by_user_id?: string | null;
}
export interface SalesPlanChange {
  id: string;
  actor_user_id?: string | null;
  actor_email?: string | null;
  field_name: string;
  old_value?: string | null;
  new_value?: string | null;
  note?: string | null;
  created_at?: string | null;
}
export interface SalesPlan {
  id: string;
  user_id: string;
  fiscal_year: number;
  cadence: "month" | "quarter" | "year";
  start_month?: number | null;
  display_name?: string | null;
  status: string;
  narrative: string;
  currency: string;
  territory_id?: string | null;
  territory_name?: string | null;
  created_by_user_id?: string | null;
  updated_by_user_id?: string | null;
  created_by_email?: string | null;
  updated_by_email?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  agreed_at?: string | null;
  agreed_by_user_id?: string | null;
  seller_agreed_at?: string | null;
  item_ids: string[];
  periods: SalesPlanPeriod[];
  milestones: SalesPlanMilestone[];
  changes: SalesPlanChange[];
  issues: Array<{ id: string; issued_by_user_id?: string | null; issued_at?: string | null }>;
}
export interface SalesPlanResponse {
  plan: SalesPlan | null;
  catalogue: KpiItem[];
  catalogue_changes: KpiItemChange[];
  plan_owners?: Array<{ user_id: string; display_name: string }>;
  can_edit?: boolean;
}
export interface SalesPlanUpdate {
  fiscal_year: number;
  cadence?: string | null;
  start_month?: number | null;
  narrative?: string | null;
  currency?: string | null;
  periods?: Array<{
    period_start: string;
    period_end: string;
    sales_target: string;
    item_ids?: string[];
    lines: Array<{ item_id: string; target_count: string }>;
    customs?: Array<{ id?: string | null; title: string; description: string; hours: string }>;
  }>;
  milestones?: Array<{ id?: string | null; title: string; notes: string; confirmed: boolean }>;
  item_ids?: string[];
}
export interface ForecastPerson {
  user_id: string;
  display_name: string;
  reports_to_user_id?: string | null;
  yearly_target: string;
  quarterly_target: string;
  monthly_target: string;
  ytd_won: string;
  qtd_won: string;
  pct: number;
  commit_weighted?: string;
  upside_weighted?: string;
  pipeline_weighted?: string;
  months: Array<{ month: number; label: string; target: string; won: string }>;
  quarters: Array<{ quarter: number; label: string; target: string; won: string }>;
  target_cadence?: "month" | "quarter" | "year";
  plan_status?: string | null;
}

export interface ForecastTeam {
  manager_user_id: string | null;
  display_name: string;
  member_count: number;
  member_ids: string[];
  yearly_target: string;
  ytd_won: string;
  commit_weighted: string;
  upside_weighted: string;
  pipeline_weighted: string;
  pct: number;
}

export interface ForecastCategory {
  deals: number;
  unweighted: string;
  weighted: string;
  amounts?: ForecastAmount[];
}

export interface Forecast {
  basis: string;
  year?: number;
  period_start?: string;
  period_end?: string;
  view?: "rollup" | "detail";
  can_rollup?: boolean;
  pipeline: Array<{ stage: string; deals: number; unweighted: string; weighted: string; amounts?: ForecastAmount[] }>;
  currency_totals?: ForecastAmount[];
  categories?: {
    commit: ForecastCategory;
    upside: ForecastCategory;
    pipeline: ForecastCategory;
  };
  weighted_total: string;
  unweighted_total: string;
  target_total: string;
  territory_targets?: Array<{ territory_id: string; yearly_target: string }>;
  people?: ForecastPerson[];
  teams?: ForecastTeam[];
}

export interface StageEntryRule {
  id?: string;
  stage: string;
  enabled: boolean;
  create_cycle_task: boolean;
  task_title: string;
  due_hours: number;
  queue_email_draft: boolean;
  email_subject?: string | null;
  email_body?: string | null;
}
export interface RecycleItem {
  id: string;
  source: string;
  label: string;
  title: string | null;
  detail: string | null;
  soft_deleted_at: string | null;
}
export interface MailboxAccount {
  id: string;
  label: string;
  host: string;
  port: number;
  username: string;
  use_ssl: boolean;
  last_tested_at: string | null;
  last_error: string | null;
}
export interface MailboxInput {
  label?: string;
  host: string;
  port?: number;
  username: string;
  password: string;
  use_ssl?: boolean;
}
/** Tokens by touchpoint. Only sent when the organization uses its own AI connection. */
export type AiUsageArea = {
  key: "leadgen" | "mailbox" | "loxley";
  label: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
};

/** One paid AI add-on's use this month. Money fields are null until an offer has been paid. */
export type AiAllowance = {
  addon: "leadgen" | "loxley" | "mailbox_ai";
  label: string;
  used_percent: number;
  /** What used it up: the unit allowance (e.g. leads) or the AI included with the add-on. */
  limited_by: "units" | "ai" | null;
  units: { used: number; limit: number; name: string } | null;
  detail: string | null;
  currency: string | null;
  monthly_cents: number | null;
  used_cents: number | null;
  remaining_cents: number | null;
};

export type AiUsageSummary = {
  using_platform: boolean;
  resets_on?: string;
  allowances?: AiAllowance[];
  input_tokens?: number;
  output_tokens?: number;
  areas?: AiUsageArea[];
};

export type AiConnection = {
  endpoint: string;
  model: string;
  key_last4: string;
  updated_at: string | null;
};

export type AiTestResult = {
  ok: boolean;
  reply?: string;
  model?: string;
  source?: string;
  error?: string;
  code?: string;
};

